import { useMemo } from 'react'
import { View, Text, Pressable } from 'react-native'
import { Building2, ChevronDown } from 'lucide-react-native'
import { useTheme } from '../../../src/theme/useTheme'
import type { LandingNavItem, OrganizationMenuItem } from './LandingNewNav'
import { getLandingNavLinksStyles } from './LandingNavLinks.styles'

interface NavLinksTranslations {
  features: { title: string }
  howItWorks: { title: string }
  siteNav: { packs: string; organizations: string; contact: string }
}

interface LandingNavLinksProps {
  isCompact: boolean
  /** When true, center row is empty (e.g. org admin on web desktop uses sidebar only). */
  suppressCenterNav?: boolean
  onPressFeatures?: () => void
  onPressHowItWorks?: () => void
  onPressPricing?: () => void
  onPressContact?: () => void
  primaryNavItems?: LandingNavItem[]
  organizationSingleLink: OrganizationMenuItem | null
  hasOrganizationMenu: boolean
  organizationMenuItems?: OrganizationMenuItem[]
  organizationsOpen: boolean
  openOrganizations: () => void
  scheduleCloseOrganizations: () => void
  toggleOrganizations: () => void
  onOrganizationItemPress: (onPress: () => void) => void
  t: NavLinksTranslations
}

export function LandingNavLinks({
  isCompact,
  suppressCenterNav,
  onPressFeatures,
  onPressHowItWorks,
  onPressPricing,
  onPressContact,
  primaryNavItems,
  organizationSingleLink,
  hasOrganizationMenu,
  organizationMenuItems,
  organizationsOpen,
  openOrganizations,
  scheduleCloseOrganizations,
  toggleOrganizations,
  onOrganizationItemPress,
  t,
}: LandingNavLinksProps) {
  const theme = useTheme()
  const styles = useMemo(() => getLandingNavLinksStyles(theme), [theme])
  if (suppressCenterNav) {
    return <View style={[styles.navLinksRow, isCompact && styles.navLinksRowCompact]} />
  }
  if (primaryNavItems?.length) {
    return (
      <View style={[styles.navLinksRow, isCompact && styles.navLinksRowCompact]}>
        {primaryNavItems.map((item) => (
          <Pressable
            key={item.key}
            accessibilityRole="link"
            onPress={item.onPress}
            style={({ hovered }) => [styles.navLink, hovered && styles.navLinkHovered]}
          >
            {({ hovered }) => (
              <Text style={[styles.navLinkText, hovered && styles.navLinkTextHovered]}>
                {item.label}
              </Text>
            )}
          </Pressable>
        ))}
      </View>
    )
  }

  return (
    <View style={[styles.navLinksRow, isCompact && styles.navLinksRowCompact]}>
      {onPressFeatures ? (
        <Pressable
          accessibilityRole="link"
          onPress={onPressFeatures}
          style={({ hovered }) => [styles.navLink, hovered && styles.navLinkHovered]}
        >
          {({ hovered }) => (
            <Text style={[styles.navLinkText, hovered && styles.navLinkTextHovered]}>
              {t.features.title}
            </Text>
          )}
        </Pressable>
      ) : null}
      {onPressHowItWorks ? (
        <Pressable
          accessibilityRole="link"
          onPress={onPressHowItWorks}
          style={({ hovered }) => [styles.navLink, hovered && styles.navLinkHovered]}
        >
          {({ hovered }) => (
            <Text style={[styles.navLinkText, hovered && styles.navLinkTextHovered]}>
              {t.howItWorks.title}
            </Text>
          )}
        </Pressable>
      ) : null}
      {onPressPricing ? (
        <Pressable
          accessibilityRole="link"
          onPress={onPressPricing}
          style={({ hovered }) => [styles.navLink, hovered && styles.navLinkHovered]}
        >
          {({ hovered }) => (
            <Text style={[styles.navLinkText, hovered && styles.navLinkTextHovered]}>
              {t.siteNav.packs}
            </Text>
          )}
        </Pressable>
      ) : null}
      {organizationSingleLink ? (
        <Pressable
          accessibilityRole="link"
          onPress={() => onOrganizationItemPress(organizationSingleLink.onPress)}
          style={({ hovered }) => [styles.navLink, hovered && styles.navLinkHovered]}
        >
          {({ hovered }) => (
            <Text style={[styles.navLinkText, hovered && styles.navLinkTextHovered]}>
              {organizationSingleLink.label}
            </Text>
          )}
        </Pressable>
      ) : hasOrganizationMenu ? (
        <Pressable
          style={[styles.orgMenuWrap, isCompact && styles.orgMenuWrapCompact]}
          onHoverIn={isCompact ? undefined : openOrganizations}
          onHoverOut={isCompact ? undefined : scheduleCloseOrganizations}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ expanded: organizationsOpen }}
            onPress={isCompact ? toggleOrganizations : openOrganizations}
            onHoverIn={isCompact ? undefined : openOrganizations}
            style={({ hovered }) => [
              styles.navLink,
              styles.orgTrigger,
              (hovered || organizationsOpen) && styles.navLinkHovered,
            ]}
          >
            {({ hovered }) => (
              <>
                <Text
                  style={[
                    styles.navLinkText,
                    (hovered || organizationsOpen) && styles.navLinkTextHovered,
                  ]}
                >
                  {t.siteNav.organizations}
                </Text>
                <ChevronDown
                  size={14}
                  color={hovered || organizationsOpen ? theme.primaryDark : theme.gray[800]}
                />
              </>
            )}
          </Pressable>

          {organizationsOpen ? (
            <View
              style={[styles.orgDropdown, isCompact && styles.orgDropdownCompact]}
              onPointerEnter={isCompact ? undefined : openOrganizations}
              onPointerLeave={isCompact ? undefined : scheduleCloseOrganizations}
            >
              <View style={styles.orgDropdownHoverPad}>
                <View style={styles.orgDropdownInner}>
                  {organizationMenuItems?.map((item) => (
                    <Pressable
                      key={item.key}
                      accessibilityRole="link"
                      onHoverIn={isCompact ? undefined : openOrganizations}
                      onPress={() => onOrganizationItemPress(item.onPress)}
                      style={({ hovered }) => [
                        styles.orgDropdownItem,
                        hovered && styles.orgDropdownItemHovered,
                      ]}
                    >
                      <View style={styles.orgDropdownIcon}>
                        <Building2 size={17} color={theme.primary} />
                      </View>
                      <View style={styles.orgDropdownCopy}>
                        <Text style={styles.orgDropdownTitle}>{item.label}</Text>
                        <Text style={styles.orgDropdownDescription}>{item.description}</Text>
                      </View>
                    </Pressable>
                  ))}
                </View>
              </View>
            </View>
          ) : null}
        </Pressable>
      ) : null}
      {onPressContact ? (
        <Pressable
          accessibilityRole="link"
          onPress={onPressContact}
          style={({ hovered }) => [styles.navLink, hovered && styles.navLinkHovered]}
        >
          {({ hovered }) => (
            <Text style={[styles.navLinkText, hovered && styles.navLinkTextHovered]}>
              {t.siteNav.contact}
            </Text>
          )}
        </Pressable>
      ) : null}
    </View>
  )
}
